'use client'

import { useRef, useState } from 'react'
import { FileUp, Loader2, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'

export type ForumFileInput = {
  data: string
  name: string
}

const MAX_BYTES = 4 * 1024 * 1024
const ALLOWED_EXTENSIONS = /\.(eml|txt|log|csv)$/i

export function ForumFilePicker({
  value,
  onChange,
  disabled = false,
}: {
  value: ForumFileInput | null
  onChange: (value: ForumFileInput | null) => void
  disabled?: boolean
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [reading, setReading] = useState(false)
  const [error, setError] = useState('')

  async function selectFile(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    setError('')
    if (!ALLOWED_EXTENSIONS.test(file.name)) {
      setError('Choose an .eml, .txt, .log, or .csv file.')
      return
    }
    if (file.size > MAX_BYTES) {
      setError('Diagnostic files must be 4 MB or smaller.')
      return
    }
    setReading(true)
    try {
      const data = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(String(reader.result || ''))
        reader.onerror = () => reject(new Error('Could not read that file.'))
        reader.readAsDataURL(file)
      })
      onChange({ data, name: file.name })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not read that file.')
    } finally {
      setReading(false)
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-3">
        <input
          ref={inputRef}
          type="file"
          accept=".eml,.txt,.log,.csv"
          className="sr-only"
          disabled={disabled || reading}
          onChange={selectFile}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled || reading}
          onClick={() => inputRef.current?.click()}
        >
          {reading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
          {value ? 'Replace diagnostic file' : 'Attach diagnostic file'}
        </Button>
        <span className="text-xs text-muted-foreground">.eml, .txt, .log, or .csv · up to 4 MB</span>
      </div>
      <p className="text-xs leading-relaxed text-muted-foreground">
        Diagnostic files stay private until a moderator reviews them.
      </p>
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      {value && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-background px-3 py-2">
          <span className="min-w-0 truncate text-sm">{value.name}</span>
          <Button
            type="button"
            variant="ghost"
            size="xs"
            disabled={disabled}
            onClick={() => onChange(null)}
          >
            <Trash2 className="h-3.5 w-3.5" />
            Remove
          </Button>
        </div>
      )}
    </div>
  )
}
